import { create } from 'zustand';
import { supabase, handleSupabaseError, startConnectionMonitoring, stopConnectionMonitoring } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  setUser: (user: User | null) => void;
  checkAdmin: () => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  initialize: () => void;
  cleanup: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAdmin: false,
  loading: true,
  error: null,
  initialize: () => {
    startConnectionMonitoring();
  },
  cleanup: () => {
    stopConnectionMonitoring();
  },
  setUser: async (user) => {
    set({ user, loading: true, error: null });
    if (user) {
      try {
        await get().checkAdmin();
      } catch (error) {
        console.error('Error in setUser admin check:', error);
        set({ 
          isAdmin: false, 
          loading: false, 
          error: 'Failed to verify admin status. Please try again.' 
        });
      }
    } else {
      set({ isAdmin: false, loading: false });
    }
  },
  checkAdmin: async () => {
    const user = get().user;
    if (!user) {
      set({ isAdmin: false, loading: false, error: null });
      return;
    }

    try {
      set({ loading: true, error: null });

      // First ensure we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new Error('Failed to get session: ' + sessionError.message);
      }

      if (!session) {
        set({ isAdmin: false, loading: false, error: null });
        return;
      }

      // Check admin status with retry logic
      const { data: adminUser } = await supabase
        .from('admin_users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      set({ 
        isAdmin: !!adminUser, 
        loading: false, 
        error: null 
      });
    } catch (error) {
      // Ensure we have a meaningful error message
      const errorMessage = error instanceof Error 
        ? error.message 
        : typeof error === 'object' && error !== null && 'message' in error
          ? String(error.message)
          : 'Failed to check admin status';

      console.error('Error checking admin status:', error);
      
      set({ 
        isAdmin: false, 
        loading: false, 
        error: errorMessage 
      });

      // Re-throw for upstream handling
      throw new Error(errorMessage);
    }
  },
  signOut: async () => {
    try {
      set({ loading: true, error: null });

      // Always clear the local state first
      set({ 
        user: null, 
        isAdmin: false, 
        loading: false, 
        error: null 
      });
      
      // Then attempt to sign out from Supabase
      // Even if this fails, the local state is already cleared
      await supabase.auth.signOut().catch(error => {
        // Just log the error but don't set it in state
        // since we've already cleared the local state
        console.error('Supabase sign out error:', error);
      });

    } catch (error) {
      // This catch block should never be reached since we're catching
      // the signOut error above, but keep it as a safety net
      console.error('Unexpected sign out error:', error);
      
      // Ensure state is cleared even in case of unexpected errors
      set({ 
        user: null, 
        isAdmin: false, 
        loading: false,
        error: null
      });
    }
  },
  clearError: () => set({ error: null }),
}));